import { MouseEventHandler } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import { Button as MuiButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Check, UserCheck } from 'ts-react-feather-icons';

type Props = {
  children: JSX.Element | string;
  color?: 'primary' | 'secondary' | 'success' | 'info';
  colorVariant?: 'light';
  className?: string;
  disabled?: boolean;
  form?: string;
  id?: string;
  onClick?: MouseEventHandler;
  size?: 'tiny' | 'xsmall' | 'small' | 'medium' | 'large';
  type?: 'button' | 'reset' | 'submit';
};

type AssignProps = {
  label: string;
  disabled?: boolean;
  locked?: boolean;
  onClick?: MouseEventHandler;
};

const useStyles = makeStyles((theme) => ({
  success: {
    '&.MuiButton-contained.Mui-disabled': {
      backgroundColor: '#7AE5A6',
    },
    backgroundColor: '#7AE5A6',
    '&:hover': {
      backgroundColor: '#7AE5A6',
    },
  },
  info: {
    backgroundColor: '#DBE5EE',
  },
  successlight: {
    backgroundColor: '#CAF6D9',
  },
  primarylight: {
    backgroundColor: '#DAF4FE',
  },
  secondarylight: {
    backgroundColor: '#FCF2C5',
  },
  disabledIcon: {
    color: 'rgba(0, 0, 0, 0.87)',
  },
  disabledLabel: {
    color: 'rgba(0, 0, 0, 0.87)',
    paddingLeft: 5,
  },
  fullScreenOnlyIcon: {
    height: '24px',
    display: 'inline',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  tiny: {
    padding: 0,
  },
  xsmall: {
    padding: 3,
  },
  assignButton: {
    backgroundColor: '#1C2541',
    color: 'white',
    fontWeight: 400,
    borderRadius: '100px',
    width: '76px',
    height: '24px',
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
    padding: '8px 16px',
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  completedButton: {
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
  pendingButton: {
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
}));

export function AssignButton({ onClick, label, locked, disabled }: AssignProps) {
  const classes = useStyles();
  return (
    <MuiButton
      className={classes.assignButton}
      size="small"
      startIcon={locked && <LockIcon fontSize="small" aria-hidden={true} />}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <Typography variant="body2">{label}</Typography>
    </MuiButton>
  );
}

export function CompletedButton() {
  const classes = useStyles();
  return (
    <Button className={classes.completedButton} color="success" disabled={true} size="small">
      <>
        <span className={classes.fullScreenOnlyIcon}>
          <Check />
        </span>
        <span className={classes.disabledLabel}>Completed</span>
      </>
    </Button>
  );
}

export function PendingButton() {
  const classes = useStyles();
  return (
    <Button className={classes.pendingButton} color="info" disabled={true} size="small">
      <>
        <span className={classes.fullScreenOnlyIcon}>
          <UserCheck />
        </span>
        <span className={classes.disabledLabel}>Pending</span>
      </>
    </Button>
  );
}

export function Button({
  children,
  color,
  className,
  disabled,
  form,
  id,
  onClick,
  size,
  type,
  colorVariant,
}: Props) {
  const classes = useStyles();
  let classColorOverride;
  if (colorVariant) {
    const classkey = color + colorVariant;
    classColorOverride = classes[classkey as keyof typeof classes];
  }
  let classSizeOverride;
  if (size && (size === 'tiny' || size === 'xsmall')) {
    classSizeOverride = classes[size];
    size = 'small';
  }
  if (color === 'success' || color === 'info') {
    classColorOverride = classes[color as keyof typeof classes];
    color = 'primary';
  }
  color = color ? color : 'primary';
  type = type ? type : 'button';
  return (
    <MuiButton
      id={id}
      className={clsx(className, classColorOverride, classSizeOverride)}
      variant="contained"
      color={color}
      disabled={disabled ? true : false}
      form={form}
      size={size ? size : 'large'}
      type={type}
      onClick={onClick}
    >
      {children}
    </MuiButton>
  );
}
